import { fetch, ResponseError } from '@/common/request';

const ASR_TEXT_MARKER = '<asr_text>';

export interface AudioTranscriptionData {
  text: string;
  duration?: number;
  rawText?: string;
}

export interface AudioTranscriptionOptions {
  signal?: AbortSignal;
}

export class AudioTranscriptionError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AudioTranscriptionError';
    this.status = status;
  }
}

export function getTranscriptionText(data: AudioTranscriptionData): string {
  const responseText = data.text?.trim() ?? '';
  const markerIndex = responseText.indexOf(ASR_TEXT_MARKER);

  return markerIndex >= 0 ? responseText.slice(markerIndex + ASR_TEXT_MARKER.length).trim() : responseText;
}

const getRequestError = (error: unknown) => {
  if (!(error instanceof ResponseError)) return undefined;

  const body = error.cause.body;
  const message = isResponseObject(body)
    ? (body.msg ?? body.message ?? error.cause.errorMessage)
    : error.cause.errorMessage;
  return {
    message: typeof message === 'string' && message.trim() ? message.trim() : undefined,
    status: error.cause.status,
  };
};

const isBusinessFailure = (response: Record<string, unknown>) =>
  response.success === false || (typeof response.code === 'number' && response.code !== 0);

const getResponseCode = (response: Record<string, unknown>) => (typeof response.code === 'number' ? response.code : 0);

const getResponseMessage = (response: Record<string, unknown>, fallback: string) =>
  typeof response.msg === 'string' && response.msg.trim() ? response.msg.trim() : fallback;

const isResponseObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const validateResponse = (value: unknown): Record<string, unknown> => {
  if (
    !isResponseObject(value) ||
    (value.success !== undefined && typeof value.success !== 'boolean') ||
    (value.code !== undefined && (typeof value.code !== 'number' || !Number.isFinite(value.code))) ||
    (value.msg !== undefined && typeof value.msg !== 'string') ||
    (value.timestamp !== undefined && (typeof value.timestamp !== 'number' || !Number.isFinite(value.timestamp)))
  ) {
    throw new AudioTranscriptionError(0, '语音识别服务返回的数据格式不正确');
  }
  return value;
};

export async function getAsrStatus(options: AudioTranscriptionOptions = {}): Promise<boolean> {
  const result = await fetch<unknown>('/api/asr/status', {
    method: 'POST',
    signal: options.signal,
  }).onError(() => false);

  const response = validateResponse(result);

  if (isBusinessFailure(response)) {
    throw new AudioTranscriptionError(getResponseCode(response), getResponseMessage(response, '查询语音识别状态失败'));
  }

  if (!isResponseObject(response.data) || typeof response.data.enabled !== 'boolean') {
    throw new AudioTranscriptionError(getResponseCode(response), '语音识别状态返回的数据格式不正确');
  }
  return response.data.enabled;
}

export async function transcribeAudio(
  file: File,
  options: AudioTranscriptionOptions = {},
): Promise<AudioTranscriptionData> {
  const formData = new FormData();
  formData.append('file', file);

  let result: unknown;

  try {
    result = await fetch<unknown>('/api/asr/transcribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      body: formData,
      signal: options.signal,
    }).onError(() => false);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;

    if (error instanceof SyntaxError) {
      throw new AudioTranscriptionError(0, '语音识别服务返回了无法解析的响应');
    }

    const requestError = getRequestError(error);
    if (requestError) {
      throw new AudioTranscriptionError(requestError.status, requestError.message ?? '语音识别请求失败');
    }

    throw new AudioTranscriptionError(0, '无法连接语音识别服务');
  }

  const response = validateResponse(result);

  if (isBusinessFailure(response)) {
    throw new AudioTranscriptionError(getResponseCode(response), getResponseMessage(response, '语音识别失败'));
  }

  const data = response.data;
  if (
    !isResponseObject(data) ||
    typeof data.text !== 'string' ||
    (data.duration !== undefined && (typeof data.duration !== 'number' || !Number.isFinite(data.duration))) ||
    (data.rawText !== undefined && typeof data.rawText !== 'string')
  ) {
    throw new AudioTranscriptionError(getResponseCode(response), '语音识别服务返回的数据格式不正确');
  }

  return data as unknown as AudioTranscriptionData;
}
